import { useState } from "react";
import { Save, X } from "lucide-react";
import { EmojiPicker } from "./pickers/EmojiPicker";
import { LucideIconPicker } from "./pickers/IconPickers";
import { ModalColorPicker } from "./pickers/ModalColorPicker";
import { uid } from "../utils/storage";
import { todayStr } from "../utils/dateHelpers";
import { pickCategoryColor } from "../utils/categoryColor";

// ─── INLINE CATEGORY CREATOR ────────────────────────────────────────────────
// A compact version of the Spaces tab's "New Category" form (same fields —
// emoji/icon toggle, name, color picker), meant to expand inline underneath
// a habit/task/event form's category picker so a brand-new category can be
// created without leaving the panel. `S` is the host form's own (locally
// shadowed) style set, so this reads as part of that panel, not Spaces.
export function InlineCategoryCreator({ categories, setCategories, savedColors = [], addSavedColor = () => {}, onCreated, onCancel, S }) {
  const [form, setForm] = useState({ emoji: "🎯", phosphorIcon: "", title: "", color: "", _iconMode: false });

  const save = () => {
    if (!form.title.trim()) return;
    const usedColors = categories.map(c => c.color);
    const autoColor = pickCategoryColor(usedColors, categories.length);
    const newCat = {
      id: uid(), emoji: form.emoji, phosphorIcon: form.phosphorIcon,
      title: form.title.trim(), color: form.color || autoColor, createdAt: todayStr(),
    };
    setCategories(p => [...p, newCat]);
    if (form.color) addSavedColor(form.color);
    onCreated(newCat);
  };

  return (
    <div style={{
      background: "var(--c-bg-card)", borderRadius: 14, padding: 12, marginTop: 8,
      display: "flex", flexDirection: "column", gap: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    }}>

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {form._iconMode
          ? <LucideIconPicker value={form.phosphorIcon || ""} color={form.color || "#d4789a"} onChange={icon => setForm(p => ({ ...p, phosphorIcon: icon }))} onSwitch={() => setForm(p => ({ ...p, _iconMode: false, phosphorIcon: "", _openPicker: true }))} openOnMount={!!form._openPicker} />
          : <EmojiPicker value={form.emoji} onChange={e => setForm(p => ({ ...p, emoji: e }))} onSwitch={() => setForm(p => ({ ...p, _iconMode: true, _openPicker: true }))} openOnMount={!!form._openPicker} />
        }
        <input type="text" placeholder="Category name" value={form.title}
          onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
          style={{ ...S.input, flex: 1 }} />
      </div>

      <ModalColorPicker currentColor={form.color} onColorChange={c => setForm(p => ({ ...p, color: c }))} onSaveColor={addSavedColor} savedColors={savedColors} S={S} />

      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" onClick={save} disabled={!form.title.trim()} className="clarity-add-btn"
          style={{ ...S.addBtn, flex: 1, opacity: form.title.trim() ? 1 : 0.5, cursor: form.title.trim() ? "pointer" : "not-allowed" }}>
          <Save size={14} style={{marginRight:4}} />Save category
        </button>
        <button type="button" onClick={onCancel} style={S.btn}><X size={14} style={{marginRight:4}} />Cancel</button>
      </div>
    </div>
  );
}
